"use client";

import { useSearchParams, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAllPlacesWithReviews } from "@/api/places";
import { getZoomForAreaBounds } from "@/utils/map-area";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { enqueueSnackbar } from "notistack";
import { getAllUserFollowing, getUsersForTagging } from "@/api/posts";
import { OriginModal, Modal } from "@/components/origin-modal";
import ContentViewModal from "@/components/content-comps/content-view-modal";
import { openContentDetail } from "@/utils/content-navigation";
import { ROUTES, buildPlaceListPath } from "@/constants/routes";
import {
  removeQueryParamWithoutReload,
  setQueryParamWithoutReload,
} from "@/utils/client-navigation";
import PlaceList from "@/components/places-comps/place-list";
import PlaceMapMaplibre from "@/components/places-comps/place-map-maplibre";
import { useTranslations } from "@/lib/static-intl";
import { PlaceAddModal } from "@/components/places-comps/places-add-modal";
import { fallbackLocation } from "./constants";
import {
  getBrowserUserLocation,
  getStoredUserLocation,
  isNearbyLocation,
  normalizeLocation,
  persistUserLocation,
  resolveInitialUserLocation,
} from "./location";
import {
  addDistanceToPlaces,
  buildPlaceMapMarkers,
  getPlaceResultKeys,
  getPlacesTotalCount,
  mergePlacesByIdentifier,
} from "./place-results";
import { loadSelectedPlaceViewState } from "./selected-place-view";
import { useMapPinRefresh } from "./use-map-pin-refresh";

const getInitialLandingCenter = (initialPlacesView) => {
  const latitude = Number(initialPlacesView?.mapCenter?.lat);
  const longitude = Number(initialPlacesView?.mapCenter?.lng);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { lat: latitude, lng: longitude };
};

const getInitialLandingPlaces = (initialPlacesView) =>
  Array.isArray(initialPlacesView?.places) ? initialPlacesView.places : [];

const PlacesDesktopScreen = ({ initialPlacesView = null }) => {
  const apiT = useTranslations("api");
  const { user } = useAuthContext();
  const router = useRouter();
  const searchParams = useSearchParams();
  const placeIdInList = searchParams.get("view-place-in-list");
  const viewPlaceId = searchParams.get("view-place");
  const rawSearchPlace = searchParams.get("search-place");

  // 주소로 지도를 특정 자리에 열 수 있게 한다: /places?lat=37.5&lng=126.9&zoom=16
  //
  // 왜 필요한가: 어드민 "상권 이름" 표에서 좌표를 눌러 그 자리를 확인해야 하고,
  // 앞으로 상권 칩을 눌렀을 때도 같은 방식으로 이동한다. 이 파라미터가 없어서
  // 어드민 링크가 눌러도 아무 일이 없었다(2026-08-07).
  const urlLat = Number(searchParams.get("lat"));
  const urlLng = Number(searchParams.get("lng"));
  const urlZoom = Number(searchParams.get("zoom"));
  const urlCenter =
    Number.isFinite(urlLat) &&
    Number.isFinite(urlLng) &&
    Math.abs(urlLat) <= 90 &&
    Math.abs(urlLng) <= 180 &&
    (urlLat !== 0 || urlLng !== 0)
      ? { lat: urlLat, lng: urlLng }
      : null;

  const initialLandingCenter = getInitialLandingCenter(initialPlacesView);
  const initialLandingPlaces = getInitialLandingPlaces(initialPlacesView);
  const initialLandingSearchQuery = initialPlacesView?.searchQuery || "";
  const hasInitialLandingView = Boolean(
    initialLandingCenter || initialLandingSearchQuery || initialLandingPlaces.length
  );
  const isHotpleLanding =
    initialPlacesView?.rankingMode === "play-tag-video-count";
  const searchPlace = hasInitialLandingView ? null : rawSearchPlace;

  const [userLocation, setUserLocation] = useState(
    urlCenter
      ? { latitude: urlCenter.lat, longitude: urlCenter.lng }
      : initialLandingCenter
      ? {
          latitude: initialLandingCenter.lat,
          longitude: initialLandingCenter.lng,
        }
      : null
  );
  const [userFollowing, setUserFollowing] = useState([]);
  const [mapCenter, setMapCenter] = useState({
    lat: urlCenter?.lat || initialLandingCenter?.lat || fallbackLocation.latitude,
    lng: urlCenter?.lng || initialLandingCenter?.lng || fallbackLocation.longitude,
  });
  const [mapZoom, setMapZoom] = useState(
    (urlCenter && Number.isFinite(urlZoom) && urlZoom >= 3 && urlZoom <= 20 ? urlZoom : null) ||
      (urlCenter ? 16 : null) ||
      initialPlacesView?.mapZoom ||
      18
  );
  const [mapCameraJumpKey, setMapCameraJumpKey] = useState(0);
  const [mapBounds, setMapBounds] = useState(null);
  const [activeSearchBounds, setActiveSearchBounds] = useState(null);
  const [mapRadius, setMapRadius] = useState(1000);
  const [searchQuery, setSearchQuery] = useState(
    searchPlace || initialLandingSearchQuery || ""
  );
  const [pageNo, setPageNo] = useState(1);
  const [placeFilters, setPlaceFilters] = useState(null);
  const [totalPlacesAvailable, setTotalPlacesAvailable] = useState(
    initialPlacesView?.totalPlaces || initialLandingPlaces.length || 0
  );

  // 지도가 지금 보여주는 동네 목록. 목록도 같은 것을 보여 준다(추가 조회 없음).
  const [areaView, setAreaView] = useState({ chips: [], level: null });
  const handleAreaChipsChange = useCallback((next) => {
    setAreaView((prev) =>
      prev.level === next?.level && prev.chips === next?.chips ? prev : { chips: next?.chips || [], level: next?.level || null }
    );
  }, []);
  const handleAreaSelect = useCallback(
    (area) => {
      if (!area) return;
      setMapCenter({ lat: Number(area.lat), lng: Number(area.lng) });
      setMapZoom(areaView.level === "group" ? 15.6 : 17.2);
      setMapCameraJumpKey((prev) => prev + 1);
    },
    [areaView.level]
  );

  const [isLeftSectionExpanded, setIsLeftSectionExpanded] = useState(true);

  const [placeListLoader, setPlaceListLoader] = useState(
    initialLandingPlaces.length === 0
  );
  const [placeScrollLoader, setPlaceScrollLoader] = useState(true);

  const [mapRedPinCenter, setMapRedPinCenter] = useState(null);
  const [placeSearchMapPinProperties, setPlaceSearchMapPinProperties] =
    useState(null);

  const [places, setPlaces] = useState(initialLandingPlaces);
  const [markers, setMarkers] = useState(() =>
    initialLandingPlaces.length > 0
      ? buildPlaceMapMarkers(initialLandingPlaces)
      : []
  );
  const [isUsingInitialLandingPlaces, setIsUsingInitialLandingPlaces] = useState(
    initialLandingPlaces.length > 0
  );
  // The selected place, loaded ON DEMAND when it is NOT already in the top-N left
  // list (e.g. a map bounds-pin outside the list). It is shown as the TOP row of
  // the list instead of COLLAPSING the whole list to a single item.
  const [selectedPlaceRow, setSelectedPlaceRow] = useState(null);
  const matchesViewPlace = useCallback(
    (place) =>
      Boolean(viewPlaceId) &&
      getPlaceResultKeys(place).some(
        (id) => String(id) === String(viewPlaceId)
      ),
    [viewPlaceId]
  );
  const selectedPlaceInList = viewPlaceId
    ? places.find((place) => matchesViewPlace(place))
    : undefined;
  const selectedPlacePreview =
    selectedPlaceInList ||
    (selectedPlaceRow && matchesViewPlace(selectedPlaceRow)
      ? selectedPlaceRow
      : undefined);
  // Unified left-list order: keep the FULL area list but always float the
  // selected place to the top (highlighted). If the selected place is not in the
  // loaded list, prepend the on-demand-loaded row above the area list. Restores
  // to normal order on deselect. Mirrors the map/mobile parity behavior.
  const listPlaces = useMemo(() => {
    if (!viewPlaceId) return places;
    const idx = places.findIndex((place) =>
      getPlaceResultKeys(place).some((id) => String(id) === String(viewPlaceId))
    );
    if (idx > 0) {
      const reordered = places.slice();
      const [selected] = reordered.splice(idx, 1);
      return [selected, ...reordered];
    }
    if (idx === 0) return places;
    if (
      selectedPlaceRow &&
      getPlaceResultKeys(selectedPlaceRow).some(
        (id) => String(id) === String(viewPlaceId)
      )
    ) {
      return [selectedPlaceRow, ...places];
    }
    return places;
  }, [places, selectedPlaceRow, viewPlaceId]);
  const isInitialPlacesBooting =
    !isUsingInitialLandingPlaces &&
    !userLocation &&
    !searchPlace &&
    places.length === 0;
  const isPlaceListLoading = placeListLoader || isInitialPlacesBooting;

  const [isAddPlaceModalOpen, setIsAddPlaceModalOpen] = useState(false);

  const [shouldFetchPlaces, setShouldFetchPlaces] = useState(false);
  const [isLocatingUser, setIsLocatingUser] = useState(false);

  const playerRef = useRef(null);
  const popupRef = useRef(null);
  const placesRequestIdRef = useRef(0);
  const selectedPlaceRequestIdRef = useRef(0);
  const selectedPlaceLoadKeyRef = useRef("");
  const didMountPlaceFiltersRef = useRef(false);
  const skipInitialSearchQuerySyncRef = useRef(Boolean(initialLandingSearchQuery));
  const [isPiP, setIsPiP] = useState(false);
  const [popupStyle, setPopupStyle] = useState({});
  const [originalStyle, setOriginalStyle] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedPostInModal, setSelectedPostInModal] = useState(null);
  const [allUsersForTagging, setAllUsersForTagging] = useState([]);
  const {
    isOpen,
    isClosing,
    position,
    handleOriginModalTriggerClick,
    handleOriginModalClose,
    setIsOpen,
  } = OriginModal();

  const openPlaceAddModal = () => setIsAddPlaceModalOpen(true);
  const closePlaceAddModal = () => setIsAddPlaceModalOpen(false);

  const handlePostCardClick = (e, postData) => {
    openContentDetail({
      router,
      content: postData,
      contextType: "place",
    });
  };

  const closeContentModalPopup = () => {
    setIsAnimating(false);
    setPopupStyle(originalStyle);
    setTimeout(() => {
      setSelectedPostInModal(null);
    }, 300);
    handleOriginModalClose();
  };

  const handleEnablePIP = () => {
    setIsPiP(true);
  };

  const handleDisablePIP = () => {
    setIsPiP(false);
  };

  const removeQueryParam = (key) => {
    removeQueryParamWithoutReload(key, searchParams);
  };

  const addQueryParam = (key, value) => {
    setQueryParamWithoutReload(key, value, searchParams);
  };

  const routeToRootPlaceSearch = useCallback(
    (query) => {
      const nextQuery = String(query || "").trim();
      if (!nextQuery || typeof window === "undefined") return false;
      if (window.location.pathname === ROUTES.current.place) return false;

      router.push(buildPlaceListPath({ "search-place": nextQuery }));
      return true;
    },
    [router]
  );

  const updatePageNo = () => {
    setIsUsingInitialLandingPlaces(false);
    setPlaceScrollLoader(true);
    setPageNo((_pageNo) => _pageNo + 1);
  };

  const addDistanceAttribute = (_places, loc) =>
    addDistanceToPlaces(_places, loc, userLocation);

  const generateMapMarkers = useCallback((_places, options = {}) => {
    // 지역으로 옮긴 뒤에는 첫 핀으로 다시 끌고 가지 않는다.
    const { syncSearchCenter = true } = options;
    const newMarkers = buildPlaceMapMarkers(_places);

    if (
      syncSearchCenter &&
      newMarkers?.length > 0 &&
      pageNo === 1 &&
      searchPlace?.length > 0
    ) {
      const newMapCenter = {
        lat: newMarkers[0]?.geometry?.coordinates[1],
        lng: newMarkers[0]?.geometry?.coordinates[0],
      };
      if (newMapCenter !== mapCenter && !placeIdInList) {
        setMapCenter(newMapCenter);
      }
    }
    return newMarkers;
  }, [mapCenter, pageNo, placeIdInList, searchPlace]);

  const {
    mapPinResultsVisibleRef,
    refreshMapMarkersForBounds,
    resetMapPinResultsVisible,
  } = useMapPinRefresh({ generateMapMarkers, setMarkers });

  const applyLocation = (
    location,
    { persist = true, forceMapCenter = false, jump = false, zoom } = {}
  ) => {
    const normalizedLocation = normalizeLocation(location);
    if (!normalizedLocation) return;

    setUserLocation((previousLocation) =>
      isNearbyLocation(previousLocation, normalizedLocation)
        ? previousLocation
        : normalizedLocation
    );
    if (forceMapCenter || !hasInitialLandingView) {
      setMapCenter({
        lat: normalizedLocation.latitude,
        lng: normalizedLocation.longitude,
      });
    }
    if (Number.isFinite(zoom)) setMapZoom(zoom);
    if (jump) setMapCameraJumpKey((prev) => prev + 1);

    if (persist) persistUserLocation(normalizedLocation);
  };

  const fetchCurrentUserLocation = async () => {
    const { initialLocation, shouldPersistInitialLocation } =
      resolveInitialUserLocation();
    applyLocation(initialLocation, { persist: shouldPersistInitialLocation });

    try {
      const browserLocation = await getBrowserUserLocation();
      const browserResolvedLocation = normalizeLocation(browserLocation.coords);
      const currentLocation = getStoredUserLocation() || initialLocation;
      if (
        !browserResolvedLocation ||
        isNearbyLocation(currentLocation, browserResolvedLocation)
      ) {
        return;
      }

      applyLocation(browserResolvedLocation);
      return;
    } catch (error) {
      if (error?.message === "Browser does not support Geolocation.") {
        console.warn(error.message);
      } else {
        console.warn("Browser Geolocation failed:", error.message);
      }
    }
  };

  const goToCurrentLocation = async () => {
    if (isLocatingUser) return;

    setIsLocatingUser(true);

    try {
      const storedLocation = getStoredUserLocation();
      if (storedLocation) {
        applyLocation(storedLocation, {
          persist: false,
          forceMapCenter: true,
          jump: true,
          zoom: 15,
        });
      }

      const browserLocation = await getBrowserUserLocation();
      const browserResolvedLocation = normalizeLocation(browserLocation.coords);
      if (!browserResolvedLocation) return;

      applyLocation(browserResolvedLocation, {
        forceMapCenter: true,
        jump: true,
        zoom: 15,
      });
    } catch (_) {
      // Permission denial or unsupported geolocation should not interrupt map use.
    } finally {
      setIsLocatingUser(false);
    }
  };

  const fetchPlaces = async () => {
    if (!userLocation) return;
    const requestId = placesRequestIdRef.current + 1;
    placesRequestIdRef.current = requestId;

    try {
      // NOTE: we intentionally do NOT short-circuit to a "selected place only"
      // view here anymore. Selecting a place keeps the full area list and floats
      // the selected place to the top (see listPlaces + the selected-row effect),
      // so fetchPlaces always loads the surrounding area list.
      const queryParams = {
        mapCenter,
        mapBounds: activeSearchBounds,
        mapRadius,
        page: pageNo,
        filters:
          placeFilters && Object.keys(placeFilters)?.length > 0
            ? JSON.stringify(placeFilters)
            : null,
        searchQuery:
          searchQuery &&
          searchQuery?.length > 0 &&
          searchQuery !== "My Location"
            ? searchQuery
            : null,
      };
      // 단일 소스(핫플만). 예전에 있던 두 번째 외부 소스는 제거됐다:
      // it summed BOTH sources' counts into totalPlaces while
      // mergePlacesByIdentifier deduped the results, so the count overcounted
      // (예: "서울베이컨" → 핫플 1 + 외부 1 = "2 결과" 인데 줄은 1개) 문제가 있었고
      // infinite-scroll hasMore (places.length < totalPlaces) never went false →
      // endless "더 많은 장소 로드 중". Mobile is already single-source (e371f8a5).
      const response = await getAllPlacesWithReviews(queryParams);
      const totalPlaces = getPlacesTotalCount(response);
      const mergedPlaces = response?.places || [];

      if (requestId !== placesRequestIdRef.current) return;

      setTotalPlacesAvailable(totalPlaces);

      const _placesWithDistance = addDistanceAttribute(mergedPlaces, userLocation);
      const nextPlaces =
        queryParams.page === 1
          ? _placesWithDistance || []
          : mergePlacesByIdentifier(places, _placesWithDistance || []);

      setPlaces(nextPlaces);

      // 지역이 인식된 검색이면(예: "성수동 브런치") 지도를 그 동네로 옮긴다.
      // 예전에는 첫 번째 핀으로만 갔는데, 그러면 목록 1등이 동네 끝에 있을 때
      // 화면이 엉뚱한 데를 비췄다.
      const matchedArea = response?.area;
      if (matchedArea?.center && queryParams.page === 1) {
        setMapCenter({ lat: matchedArea.center.lat, lng: matchedArea.center.lng });
        setMapZoom(getZoomForAreaBounds(matchedArea.bbox));
        setMapCameraJumpKey((prev) => prev + 1);
      }

      // ⚠️ 여기에 `queryParams.mapBounds` 를 넣으면 안 된다.
      //
      // 지도를 움직이면 두 가지가 동시에 나간다 — 지도 핀(수천 개)과 목록(20개).
      // mapBounds 조건이 있으면 **목록이 도착하는 순간 지도 핀을 20개로 덮어쓴다.**
      // 둘 중 늦게 온 쪽이 이기므로, 망이 느린 곳에서는 늘 목록이 이겨서 지도에
      // 20곳만 남는다(2026-08-06 사장님 화면: 어느 확대에서나 정확히 20곳).
      // 내 자리에서는 핀이 늦게 와서 다 보였다 — 같은 코드가 사람마다 달라 보인 이유다.
      //
      // 지도를 움직였을 때 지도에 그려야 할 것은 목록 20곳이 아니라 그 화면의 모든 곳이다.
      const shouldSyncListMarkers =
        queryParams.searchQuery ||
        queryParams.page > 1 ||
        !mapPinResultsVisibleRef.current;

      if (shouldSyncListMarkers) {
        resetMapPinResultsVisible();
        setMarkers(generateMapMarkers(nextPlaces));
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      if (requestId === placesRequestIdRef.current) {
        setPlaceListLoader(false);
        setPlaceScrollLoader(false);
      }
    }
  };

  const fetchUserFollowing = async () => {
    if (user?.userId) {
      try {
        const response = await getAllUserFollowing(user?.userId);
        if (Array.isArray(response)) setUserFollowing(response);
      } catch (error) {
        enqueueSnackbar(
          error?.response?.data?.message ||
            error?.message ||
            apiT("error message"),
          { variant: "error" }
        );
      }
    }
  };

  const fetchAllUsersForTagging = async () => {
    try {
      const response = await getUsersForTagging();
      if (Array.isArray(response)) setAllUsersForTagging(response);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const handlePlaceSorting = (type) => {
    setPlaceListLoader(true);
    resetMapPinResultsVisible();
    if (type === "rating") {
      const unsortedPlaces = [...places];
      const sortedPlaces = unsortedPlaces?.toSorted(
        (a, b) => Number(b?.naverRating) - Number(a?.naverRating)
      );
      const newMarkers = generateMapMarkers(sortedPlaces);
      setMarkers(newMarkers);
      setPlaces(sortedPlaces);
    } else if (type === "distance") {
      const unsortedPlaces = [...places];
      const sortedPlaces = unsortedPlaces?.toSorted(
        (a, b) => Number(a?.distanceFromMe) - Number(b?.distanceFromMe)
      );
      const newMarkers = generateMapMarkers(sortedPlaces);
      setMarkers(newMarkers);
      setPlaces(sortedPlaces);
    } else if (type === "numOfReviews") {
      const unsortedPlaces = [...places];
      const sortedPlaces = unsortedPlaces?.toSorted(
        (a, b) =>
          Number(b?.numberOfVisitorReviews) - Number(a?.numberOfVisitorReviews)
      );
      const newMarkers = generateMapMarkers(sortedPlaces);
      setMarkers(newMarkers);
      setPlaces(sortedPlaces);
    } else if (type === "relevance") {
      const unsortedPlaces = [...places];
      const sortedPlaces = unsortedPlaces?.toSorted(
        (a, b) => Number(b?._score) - Number(a?._score)
      );
      const newMarkers = generateMapMarkers(sortedPlaces);
      setMarkers(newMarkers);
      setPlaces(sortedPlaces);
    }
    setPlaceListLoader(false);
  };

  const completePlacesReset = (
    isSearchReset = false,
    isMapLocationReset = false,
    isMapRadiusReset = true,
    options = {}
  ) => {
    setIsUsingInitialLandingPlaces(false);
    // ⚠️ 지도를 움직여서 온 새로고침이면 "핀 보임" 표시를 끄지 않는다.
    //
    // 이 표시를 끄면, 목록(20개)이 도착할 때 shouldSyncListMarkers 가 참이 되어
    // 지도 핀을 20개로 덮어쓴다. 지도를 움직이면 핀과 목록이 동시에 나가는데
    // 늦게 온 쪽이 이기므로, 핀이 느린 환경에서는 매번 목록이 이겨서 지도에
    // 20곳만 남는다(2026-08-06 사장님 화면 · 넓게 축소하면 "20곳" 하나만 남던 것).
    //
    // 지도를 움직였을 때 지도에 그려야 할 것은 목록 20곳이 아니라 그 화면의 모든 곳이다.
    // 검색·정렬 등 지도와 무관한 새로고침은 예전처럼 목록이 지도를 정한다.
    if (!options?.useMapBounds) resetMapPinResultsVisible();
    placesRequestIdRef.current += 1;
    setPlaces([]);
    if (isSearchReset) setSearchQuery("");
    setActiveSearchBounds(
      options?.useMapBounds ? options?.mapBounds || mapBounds : null
    );

    if (isMapLocationReset) {
      setMapCenter({
        lat: userLocation?.latitude,
        lng: userLocation?.longitude,
      });
    }
    if (isMapRadiusReset) setMapRadius(1000);
    setTotalPlacesAvailable(0);
    setPageNo(1);
    setShouldFetchPlaces(true);
  };

  // Auto-refresh the left place list when the user pans / zooms out the map
  // (mirrors mobile). The map component already applies the pan/zoom-out +
  // debounce guard and only calls this on genuine user gestures; here we just
  // trigger the area refetch, skipping it while a place is selected so it
  // doesn't fight the selection's collapsed list.
  const handleMapAreaAutoRefresh = (camera) => {
    if (viewPlaceId || placeIdInList) return;
    const bounds = camera?.bounds || mapBounds;
    completePlacesReset(false, false, false, {
      useMapBounds: true,
      mapBounds: bounds,
    });
  };

  const changeMapCenterAndZoomIn = (newMapCenter, options = {}) => {
    setMapCenter(newMapCenter);
    if (options.jump) {
      setMapCameraJumpKey((prev) => prev + 1);
    }
    if (Number.isFinite(options.zoom)) {
      setMapZoom(options.zoom);
      return;
    }
    setMapZoom((prev) => (prev < 19 ? 19 : prev));
  };

  useEffect(() => {
    // 주소로 특정 자리를 열었으면 현재 위치로 끌고 가지 않는다.
    // (어드민에서 좌표를 눌러 왔는데 내 위치로 튕기면 확인이 안 된다)
    if (urlCenter) return;
    fetchCurrentUserLocation();
  }, []);

  useEffect(() => {
    if (!viewPlaceId) {
      // Deselecting → the derived listPlaces reverts to normal order on its own
      // (the area list was never collapsed), so no refetch is needed. Just clear
      // the on-demand selected row + its load guard.
      selectedPlaceLoadKeyRef.current = "";
      setSelectedPlaceRow(null);
      return;
    }

    // A detail-window selection (view-place-in-list) is handled elsewhere, and a
    // place already in the loaded list just floats to the top — no separate row.
    if (placeIdInList || selectedPlaceInList) {
      selectedPlaceLoadKeyRef.current = "";
      setSelectedPlaceRow(null);
      return;
    }

    // Selected place is NOT in the loaded list (a map bounds-pin outside the
    // top-N list, or a deep link). Load ONLY that place's data and show it as the
    // TOP row above the intact area list — never collapse the list. The area list
    // stays visible (no skeleton) while the row loads.
    const loadKey = `${viewPlaceId}:${user?.userId || ""}`;
    if (selectedPlaceLoadKeyRef.current === loadKey) return;
    selectedPlaceLoadKeyRef.current = loadKey;

    const requestId = selectedPlaceRequestIdRef.current + 1;
    selectedPlaceRequestIdRef.current = requestId;

    loadSelectedPlaceViewState({
      placeId: viewPlaceId,
      userId: user?.userId,
      userLocation,
    })
      .then((selectedPlaceView) => {
        if (requestId !== selectedPlaceRequestIdRef.current) return;
        const loadedPlace = Array.isArray(selectedPlaceView?.places)
          ? selectedPlaceView.places[0]
          : null;
        setSelectedPlaceRow(loadedPlace || null);
      })
      .catch((error) => {
        if (requestId !== selectedPlaceRequestIdRef.current) return;
        selectedPlaceLoadKeyRef.current = "";
        enqueueSnackbar(
          error?.response?.data?.message ||
            error?.message ||
            apiT("error message"),
          { variant: "error" }
        );
      });
  }, [
    apiT,
    placeIdInList,
    selectedPlaceInList,
    user?.userId,
    userLocation,
    viewPlaceId,
  ]);

  useEffect(() => {
    if (
      isUsingInitialLandingPlaces &&
      pageNo === 1 &&
      !searchPlace &&
      searchQuery === initialLandingSearchQuery
    ) {
      setPlaceListLoader(false);
      setPlaceScrollLoader(false);
      return;
    }

    if (userLocation || searchPlace) {
      fetchPlaces();
    }
  }, [isUsingInitialLandingPlaces, userLocation, searchPlace, pageNo]);

  useEffect(() => {
    if (!didMountPlaceFiltersRef.current) {
      didMountPlaceFiltersRef.current = true;
      return;
    }

    if (
      isUsingInitialLandingPlaces &&
      pageNo === 1 &&
      !searchPlace &&
      searchQuery === initialLandingSearchQuery
    ) {
      return;
    }

    completePlacesReset();
  }, [placeFilters]);

  useEffect(() => {
    if (shouldFetchPlaces) {
      setPlaceListLoader(true);
      fetchPlaces();
      setShouldFetchPlaces(false);
    }
  }, [shouldFetchPlaces]);

  useEffect(() => {
    if (
      hasInitialLandingView &&
      isUsingInitialLandingPlaces &&
      searchQuery === initialLandingSearchQuery
    ) {
      skipInitialSearchQuerySyncRef.current = false;
      return;
    }

    if (skipInitialSearchQuerySyncRef.current) {
      skipInitialSearchQuerySyncRef.current = false;
      return;
    }

    if (searchQuery === "My Location") {
      let searchReset = false;
      let mapLocationReset = true;
      completePlacesReset(searchReset, mapLocationReset);
    } else if (searchQuery && searchQuery?.length > 0) {
      const nextSearchQuery = String(searchQuery).trim();
      if (routeToRootPlaceSearch(nextSearchQuery)) return;

      addQueryParam("search-place", nextSearchQuery);
      completePlacesReset();
    } else if (searchQuery === "") {
      removeQueryParam("search-place");
    }
  }, [searchQuery]);

  useEffect(() => {
    if (user?.userId) {
      fetchUserFollowing();
      fetchAllUsersForTagging();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {};
    if (selectedPostInModal) setIsOpen(true);
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedPostInModal]);
  return (
    <>
      <div className="w-full h-screen flex overflow-hidden pb-12 lg:pb-0 border-l dark:border-brown">
        <PlaceList
          areaChips={areaView.chips}
          areaLevel={areaView.level}
          onAreaSelect={handleAreaSelect}
          places={listPlaces}
          updatePageNo={updatePageNo}
          userLocation={userLocation}
          placeListLoader={isPlaceListLoading}
          addDistanceAttribute={addDistanceAttribute}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          completePlacesReset={completePlacesReset}
          totalPlaces={totalPlacesAvailable}
          addQueryParam={addQueryParam}
          removeQueryParam={removeQueryParam}
          mapCenter={mapCenter}
          mapBounds={mapBounds}
          setMapCenter={setMapCenter}
          changeMapCenterAndZoomIn={changeMapCenterAndZoomIn}
          mapRedPinCenter={mapRedPinCenter}
          setMapRedPinCenter={setMapRedPinCenter}
          placeSearchMapPinProperties={placeSearchMapPinProperties}
          setPlaceSearchMapPinProperties={setPlaceSearchMapPinProperties}
          userFollowing={userFollowing}
          setUserFollowing={setUserFollowing}
          handlePostCardClick={handlePostCardClick}
          isLeftSectionExpanded={isLeftSectionExpanded}
          placeFilters={placeFilters}
          setPlaceFilters={setPlaceFilters}
          handlePlaceSorting={handlePlaceSorting}
          openPlaceAddModal={openPlaceAddModal}
          initialPlacesView={initialPlacesView}
        />
        <PlaceMapMaplibre
          mapMarkers={markers}
          mapCenter={mapCenter}
          mapZoom={mapZoom}
          mapBounds={mapBounds}
          mapRadius={mapRadius}
          setMapBounds={setMapBounds}
          setMapCenter={setMapCenter}
          setMapZoom={setMapZoom}
          setMapRadius={setMapRadius}
          searchQuery={searchQuery}
          completePlacesReset={completePlacesReset}
          onAreaAutoRefresh={
            isHotpleLanding ? null : handleMapAreaAutoRefresh
          }
          refreshMapMarkersForBounds={
            isHotpleLanding ? null : refreshMapMarkersForBounds
          }
          onAreaChipsChange={isHotpleLanding ? null : handleAreaChipsChange}
          placeListLoader={isPlaceListLoading}
          placeScrollLoader={placeScrollLoader}
          mapRedPinCenter={mapRedPinCenter}
          changeMapCenterAndZoomIn={changeMapCenterAndZoomIn}
          placeSearchMapPinProperties={placeSearchMapPinProperties}
          mapCameraJumpKey={mapCameraJumpKey}
          userFollowing={userFollowing}
          setUserFollowing={setUserFollowing}
          handlePostCardClick={handlePostCardClick}
          addQueryParam={addQueryParam}
          isLeftSectionExpanded={isLeftSectionExpanded}
          setIsLeftSectionExpanded={setIsLeftSectionExpanded}
          userLocation={userLocation}
          selectedPlacePreview={selectedPlacePreview}
          openPlaceAddModal={openPlaceAddModal}
          onGoToCurrentLocation={goToCurrentLocation}
          isLocatingUser={isLocatingUser}
          disableCameraAnimation={isHotpleLanding}
          scrollZoom
          clusterMarkers={!isHotpleLanding}
        />
      </div>
      {isAddPlaceModalOpen && (
        <PlaceAddModal
          isOpen={isAddPlaceModalOpen}
          onClose={closePlaceAddModal}
        />
      )}
      {isOpen && (
        <Modal
          handleClose={closeContentModalPopup}
          isClosing={isClosing}
          position={position}
          isOpen={isOpen || !!selectedPostInModal}
          isPiP={isPiP}
        >
          <ContentViewModal
            ref={popupRef}
            allPosts={[]}
            closePopup={closeContentModalPopup}
            setAllPosts={() => {}}
            currentUserFollowing={userFollowing}
            setCurrentUserFollowing={setUserFollowing}
            allUsersForTagging={allUsersForTagging}
            selectedPostInModal={selectedPostInModal}
            setSelectedPostInModal={setSelectedPostInModal}
            completeRefresh={() => {}}
            isAnimating={isAnimating}
            popupStyle={popupStyle}
            isPiP={isPiP}
            setIsPiP={setIsPiP}
            playerRef={playerRef}
            handleDisablePIP={handleDisablePIP}
            handleEnablePIP={handleEnablePIP}
          />
        </Modal>
      )}
    </>
  );
};

export default PlacesDesktopScreen;
